import { useState } from 'react';

interface AlternativeDate {
  date: string;
  notes: string;
}

interface AlternativeDatesManagerProps {
  alternativeDates: AlternativeDate[];
  onChange: (dates: AlternativeDate[]) => void;
  programStartDate?: string;
  programEndDate?: string;
}

export function AlternativeDatesManager({ alternativeDates, onChange, programStartDate, programEndDate }: AlternativeDatesManagerProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [newDate, setNewDate] = useState({ date: '', notes: '' });

  const handleAddClick = () => {
    setIsAdding(true);
    setNewDate({ date: '', notes: '' });
  };

  const handleSaveNew = () => {
    if (newDate.date.trim() && newDate.notes.trim()) {
      // Validate that the date is within program bounds using consistent timezone handling
      if (programStartDate && programEndDate) {
        // Parse all dates consistently to avoid timezone issues
        const newDateObj = new Date(
          parseInt(newDate.date.split('-')[0]),
          parseInt(newDate.date.split('-')[1]) - 1,
          parseInt(newDate.date.split('-')[2])
        );
        const startDateObj = new Date(
          parseInt(programStartDate.split('-')[0]),
          parseInt(programStartDate.split('-')[1]) - 1,
          parseInt(programStartDate.split('-')[2])
        );
        const endDateObj = new Date(
          parseInt(programEndDate.split('-')[0]),
          parseInt(programEndDate.split('-')[1]) - 1,
          parseInt(programEndDate.split('-')[2])
        );
        
        if (newDateObj < startDateObj || newDateObj > endDateObj) {
          alert(`Alternative date must be between ${programStartDate} and ${programEndDate}`);
          return;
        }
      }
      
      onChange([...alternativeDates, newDate]);
      setNewDate({ date: '', notes: '' });
      setIsAdding(false);
    }
  };

  const handleCancelAdd = () => {
    setIsAdding(false);
    setNewDate({ date: '', notes: '' });
  };

  const handleRemove = (index: number) => {
    const updated = alternativeDates.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleEdit = (index: number, field: 'date' | 'notes', value: string) => {
    // If editing date, validate it's within program bounds using consistent timezone handling
    if (field === 'date' && programStartDate && programEndDate) {
      // Parse all dates consistently to avoid timezone issues
      const newDateObj = new Date(
        parseInt(value.split('-')[0]),
        parseInt(value.split('-')[1]) - 1,
        parseInt(value.split('-')[2])
      );
      const startDateObj = new Date(
        parseInt(programStartDate.split('-')[0]),
        parseInt(programStartDate.split('-')[1]) - 1,
        parseInt(programStartDate.split('-')[2])
      );
      const endDateObj = new Date(
        parseInt(programEndDate.split('-')[0]),
        parseInt(programEndDate.split('-')[1]) - 1,
        parseInt(programEndDate.split('-')[2])
      );
      
      if (newDateObj < startDateObj || newDateObj > endDateObj) {
        alert(`Alternative date must be between ${programStartDate} and ${programEndDate}`);
        return;
      }
    }
    
    const updated = alternativeDates.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    );
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900">Alternative Start Dates</h4>
          <p className="text-xs text-gray-500">
            Configure alternative start dates that students can choose during enrollment
          </p>
        </div>
        <button
          type="button"
          onClick={handleAddClick}
          className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Date
        </button>
      </div>

      {/* Existing Alternative Dates */}
      {alternativeDates.length > 0 && (
        <div className="space-y-2">
          {alternativeDates.map((item, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={item.date}
                    onChange={(e) => handleEdit(index, 'date', e.target.value)}
                    min={programStartDate}
                    max={programEndDate}
                    className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={item.notes}
                      onChange={(e) => handleEdit(index, 'notes', e.target.value)}
                      placeholder="e.g., for Kids Connection students"
                      className="flex-1 text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="inline-flex items-center p-1 border border-transparent text-sm leading-4 font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add New Alternative Date Form */}
      {isAdding && (
        <div className="border border-blue-200 rounded-lg p-3 bg-blue-50">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Date
              </label>
              <input
                type="date"
                value={newDate.date}
                onChange={(e) => setNewDate({ ...newDate, date: e.target.value })}
                min={programStartDate}
                max={programEndDate}
                className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Notes
              </label>
              <input
                type="text"
                value={newDate.notes}
                onChange={(e) => setNewDate({ ...newDate, notes: e.target.value })}
                placeholder="e.g., for Kids Connection students"
                className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              type="button"
              onClick={handleCancelAdd}
              className="px-3 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveNew}
              disabled={!newDate.date.trim() || !newDate.notes.trim()}
              className="px-3 py-1 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {alternativeDates.length === 0 && !isAdding && (
        <div className="text-center py-4 text-sm text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          No alternative start dates configured. Click "Add Date" to create one.
        </div>
      )}
    </div>
  );
} 